/**
 * What a paid press on the node card is called aloud.
 *
 * The card's six verbs were named "Deepen D", "Challenge C" — the verb and the
 * key chip read as one word — and the source check "Check again V Searches,
 * opens…", with its price nowhere in either: the one figure for the six sits in
 * the caption above them, and the check's rides a hover swap that hides it from
 * the tree at rest. A screen reader heard six presses that spend and not one
 * word of what they spend. So each press is named the way D165 named the
 * proposal chips: the visible word first, so a voice-control user can say what
 * they see, then what its price cell would say — the one number the press
 * buys (`pressEstimate`), or the status word standing where a figure would be.
 * The key moves to `aria-keyshortcuts`, which is where a key belongs.
 *
 * Pure, and read by the card's verbs and the check and survey presses alike, so
 * the six and the two cannot come to name the same state two ways.
 *
 * Whether a press is ready is asked of the ROUTE it runs on (`routeReady`), never
 * of the selection. The six verbs and the reply's chips used to read the
 * selection's key while their passes resolve the role's (`resolveFor`), so with
 * reasoning routed to a company whose key is not stored, Deepen was named with a
 * figure for a pass that refuses and asks for the key — the one misreading
 * invariant 4 exists to prevent, said aloud. The check and the survey already
 * read the check route; now every press here does.
 */

import { PROVIDERS, type ProviderEntry } from '../core/models'
import { missingKeyNeed, resolveFor, useStore, type KeyNeed } from '../core/store'
import { upNextRole, type UpNextKind } from '../core/upNext'
import { pressEstimate, STATUS_WORD } from '../core/vocabulary'

/** What the route's answer reads: the selection, the routing and the keys. */
export type RouteReadyView = Parameters<typeof resolveFor>[0]

/**
 * Whether a press of this verb has somewhere to go: the role its pass runs on
 * (`upNextRole` — the moves, the rewrite and the rethink on reasoning, the check
 * and the survey on the check route, the panel on its own) resolves to a model
 * with a key. The same question the pass asks before it spends, so the name and
 * the press cannot disagree about whether there is a key.
 */
export const routeReady = (s: RouteReadyView, verb: UpNextKind): boolean =>
  resolveFor(s, upNextRole(verb)) !== null

/** `routeReady`, subscribed: a surface re-renders when its route's answer changes. */
export const useRouteReady = (verb: UpNextKind): boolean => useStore((s) => routeReady(s, verb))

/**
 * The clause after the press's word: an estimate, or why there is none. Keyless,
 * a press either unfurls the worked example's authored branch (`demo`) and
 * spends nothing, or leads to the key prompt — a dollar figure on either would
 * price a call that will not happen. No list rate for the routed model, no
 * figure: nothing rather than a wrong one, as `VerbPrice` prints nothing.
 */
export function pressTail({
  ready,
  demo = false,
  est,
}: {
  ready: boolean
  demo?: boolean
  est: number | null
}): string | null {
  if (!ready) return (demo ? STATUS_WORD.demo : STATUS_WORD.needsKey).toLowerCase()
  return est !== null ? pressEstimate(est) : null
}

/**
 * D202's sentence for a missing key: "The source check runs on GPT‑5.6 — it needs
 * an OpenAI key." — or, for somebody's own endpoint, which is refused for its
 * address and never its key, "… runs on your own endpoint — it needs its
 * address." The key card prints it when a refused pass opens the sheet
 * (`keyAskLine`), and a press's note prints it before the press, over the route
 * `missingKeyNeed` names, so the two cannot come to name different keys. The
 * notes used to say "Needs your Anthropic key" off the selection while the pass
 * refused for, and the card asked for, a routed company's.
 */
export function missingKeyLine(house: ProviderEntry, need: KeyNeed): string {
  if (house.custom) return `${need.what} runs on your own endpoint — it needs its address.`
  const an = /^[aeiou]/i.test(house.name) ? 'an' : 'a'
  return `${need.what} runs on ${need.on} — it needs ${an} ${house.name} key.`
}

/**
 * `missingKeyLine` for the reasoning route: "Reasoning runs on <model> — it needs
 * a <house> key.", the card's own line when `runOp` refuses on that route. The
 * refusals that print before a press — the promote row's and the coach's — said
 * "runs on the model you selected" while naming the route's house, which under
 * routing is a model nobody selected.
 */
export function reasonKeyLine(s: Parameters<typeof missingKeyNeed>[0]): string {
  const { provider, need } = missingKeyNeed(s, 'reason')
  return missingKeyLine(PROVIDERS[provider], need)
}

/**
 * The word a cell shared by several presses prints where no figure applies — the
 * caption over the six verbs, which all run on the reasoning route. "Needs a key"
 * when none of them is part of the worked example, "Part of the demo" when all
 * are, and nothing where they differ: either word would be false of some press
 * under it, and each one's name already says which (`pressTail`).
 */
export const sharedWord = (inDemo: number, of: number): string | null =>
  inDemo === 0 ? STATUS_WORD.needsKey : inDemo >= of ? STATUS_WORD.demo : null

/** "Deepen · est. +$0.30" — the word, then the tail when there is one. The
 *  rewrite editor's "Re-derive from here" and "Rethink “…”" are named here too:
 *  their buttons print the running before → after pair with no word calling it
 *  an estimate, and read aloud that was a bare pair of dollar figures. */
export const pressName = (word: string, tail: string | null): string =>
  tail ? `${word} · ${tail}` : word
